import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { copyToClipboard } from '@/lib/utils/clipboard';

interface CodeSnippetProps {
  method: string;
  path: string;
  requestBody?: string;
}

type Lang = 'curl' | 'javascript' | 'python';

function generateCurl(method: string, path: string, body?: string): string {
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://tu-servidor.com';
  const url = `${baseUrl}${path}`;
  let cmd = `curl -X ${method} "${url}"`;
  cmd += ` \\\n  -H "Authorization: Bearer {token}"`;
  cmd += ` \\\n  -H "Content-Type: application/json"`;
  if (body && (method === 'POST' || method === 'PUT')) {
    cmd += ` \\\n  -d '${body}'`;
  }
  return cmd;
}

function generateJavaScript(method: string, path: string, body?: string): string {
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://tu-servidor.com';
  const url = `${baseUrl}${path}`;
  let code = `const response = await fetch("${url}", {\n`;
  code += `  method: "${method}",\n`;
  code += `  headers: {\n`;
  code += `    "Authorization": "Bearer {token}",\n`;
  code += `    "Content-Type": "application/json",\n`;
  code += `  },\n`;
  if (body && (method === 'POST' || method === 'PUT')) {
    code += `  body: JSON.stringify(${body}),\n`;
  }
  code += `});\n\n`;
  code += `const data = await response.json();\nconsole.log(data);`;
  return code;
}

function generatePython(method: string, path: string, body?: string): string {
  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://tu-servidor.com';
  const url = `${baseUrl}${path}`;
  let code = `import requests\n\n`;
  code += `headers = {\n`;
  code += `    "Authorization": "Bearer {token}",\n`;
  code += `    "Content-Type": "application/json",\n`;
  code += `}\n\n`;
  if (body && (method === 'POST' || method === 'PUT')) {
    code += `payload = ${body}\n\n`;
    code += `response = requests.${method.toLowerCase()}(\n`;
    code += `    "${url}",\n`;
    code += `    headers=headers,\n`;
    code += `    json=payload,\n`;
    code += `)\n`;
  } else {
    code += `response = requests.${method.toLowerCase()}(\n`;
    code += `    "${url}",\n`;
    code += `    headers=headers,\n`;
    code += `)\n`;
  }
  code += `\nprint(response.json())`;
  return code;
}

export function CodeSnippet({ method, path, requestBody }: CodeSnippetProps) {
  const [activeLang, setActiveLang] = useState<Lang>('curl');
  const [copied, setCopied] = useState(false);

  const langs: { id: Lang; label: string }[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'javascript', label: 'JavaScript' },
    { id: 'python', label: 'Python' },
  ];

  const generators: Record<Lang, () => string> = {
    curl: () => generateCurl(method, path, requestBody),
    javascript: () => generateJavaScript(method, path, requestBody),
    python: () => generatePython(method, path, requestBody),
  };

  const code = generators[activeLang]();

  const handleCopy = async () => {
    await copyToClipboard(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between bg-muted/50 px-3 py-1.5 border-b border-border">
        <div className="flex gap-1">
          {langs.map((lang) => (
            <button
              key={lang.id}
              onClick={() => setActiveLang(lang.id)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-colors',
                activeLang === lang.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
            >
              {lang.label}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 gap-1.5 text-xs">
          {copied ? (
            <Check className="w-3.5 h-3.5 text-green-500" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <pre className="p-4 overflow-x-auto bg-zinc-950 text-zinc-100 text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}
